import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeftIcon, HeartIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import designerService, { Designer } from '../services/designer';
import dressService from '../services/dress';
import dressLikesService from '../services/dressLikes';
import { Dress } from '../types';
import DressCard from '../components/DressCard';
import AuthenticatedImage from '../components/AuthenticatedImage';
import { useAuth } from '../contexts/AuthContext';

const DesignerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [designer, setDesigner] = useState<Designer | null>(null);
  const [dresses, setDresses] = useState<Dress[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [likedDressIds, setLikedDressIds] = useState<Set<string>>(new Set());
  
  const observer = useRef<IntersectionObserver | null>(null);
  const lastDressElementRef = useCallback((node: HTMLDivElement | null) => {
    if (loadingMore) return;
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => prevPage + 1);
      }
    });
    
    if (node) observer.current.observe(node);
  }, [loadingMore, hasMore]);

  const loadDesigner = useCallback(async () => {
    if (!id) return;
    
    try {
      const designerData = await designerService.getDesignerById(id);
      setDesigner(designerData);
    } catch (error) {
      console.error('Error loading designer:', error);
      setError('Failed to load designer details. Please try again.');
    }
  }, [id]);

  const loadDresses = useCallback(async (pageNum: number, reset: boolean = false) => {
    if (!id) return;
    
    try {
      if (pageNum === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const response = await dressService.getDresses({
        page: pageNum,
        limit: 20,
        designerIds: [id],
      });

      // Debug: Log all received dresses
      console.log('[DesignerDetail] Received dresses from API:', response.data.map(d => ({
        id: d.id,
        name: d.name,
        tiers: d.tiers,
        designerId: d.designerId
      })));

      // Backend handles tier filtering, but we keep this as defensive filtering
      let filteredDresses = response.data;

      if (reset) {
        setDresses(filteredDresses);
      } else {
        setDresses(prev => [...prev, ...filteredDresses]);
      }
      
      setHasMore(response.pagination.page < response.pagination.totalPages);
      
      // Check which dresses are liked (only if user is authenticated)
      if (user && response.data.length > 0) {
        const dressIds = response.data.map(d => d.id);
        try {
          const likedIds = await dressLikesService.checkLikes(dressIds);
          setLikedDressIds(prev => new Set([...Array.from(prev), ...likedIds]));
        } catch (error: any) {
          console.error('Failed to check likes:', error);
        }
      }
    } catch (error) {
      console.error('Error loading dresses:', error);
      if (pageNum === 1) {
        setError('Failed to load dresses. Please try again.');
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [id, user]);

  useEffect(() => {
    if (id) {
      loadDesigner();
    }
  }, [id, loadDesigner]);

  useEffect(() => {
    loadDresses(page, page === 1);
  }, [page, loadDresses]);

  const handleLikeChange = (dressId: string, isLiked: boolean) => {
    setLikedDressIds(prev => {
      const newSet = new Set(prev);
      if (isLiked) {
        newSet.add(dressId);
      } else {
        newSet.delete(dressId);
      }
      return newSet;
    });
  };

  const goBack = () => {
    window.history.back();
  };

  if (!id) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="text-center py-12">
          <p className="text-red-500">Designer ID not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="px-4 py-4">
          <button
            onClick={goBack}
            className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Go back"
          >
            <ArrowLeftIcon className="h-6 w-6 text-gray-600" />
          </button>
        </div>
      </header>

      {/* Designer Info */}
      {designer && (
        <div className="bg-white px-4 md:px-0 py-6">
          <div className="text-center">
            {/* Designer Name */}
            <h2 className="text-3xl font-serif text-gray-900 mb-6">{designer.name}</h2>
            
            {/* Designer Banner - Full width on desktop, padded on mobile */}
            {designer.bannerUrl && (
              <div className="aspect-[16/6] md:aspect-[16/4] bg-gray-100 rounded-lg md:rounded-none overflow-hidden mb-6">
                <AuthenticatedImage
                  src={designer.bannerUrl}
                  alt={`${designer.name} banner`}
                  className="object-cover w-full h-full"
                />
              </div>
            )}
            
            {/* Designer Description */}
            {designer.description && (
              <p className="text-gray-600 leading-relaxed max-w-3xl mx-auto px-4 md:px-6">{designer.description}</p>
            )}
          </div>
        </div>
      )}

      {/* Access Level Notice */}
      {user && user.catalogAccessLevel !== 'exclusive' && user.catalogAccessLevel !== 'partner' && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4 mx-4 mt-4">
          <div className="flex items-start">
            <div>
              <p className="text-sm text-green-700">
                You have limited access to our catalog. To view our <strong>Full Catalog</strong>,{' '}
                <a
                  href="https://www.theproposal.com.sg/contact/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold underline hover:text-green-800"
                >
                  Contact Us
                </a>{' '}
                for an appointment today!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      {user && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mx-4 mt-4">
          <div className="flex items-start">
            <HeartIcon className="h-5 w-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <p className="text-sm text-blue-700">
                <span className="font-semibold">Tip:</span> Double tap any dress to like it instantly! 
                You can also click the heart icon.
              </p>
              <p className="text-sm text-blue-700 mt-1">
                View all your liked dresses in your{' '}
                <Link to="/home/liked-dresses" className="font-semibold underline hover:text-blue-800">
                  Liked Dresses
                </Link>{' '}
                collection.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Dresses Grid */}
      <div className="px-4 py-4">
        {error ? (
          <div className="text-center py-12">
            <p className="text-red-500 mb-4">{error}</p>
            <button
              onClick={() => {
                setError(null);
                setPage(1);
                setDresses([]);
                setHasMore(true);
              }}
              className="btn-primary"
            >
              Try Again
            </button>
          </div>
        ) : loading && page === 1 ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm overflow-hidden animate-pulse">
                <div className="aspect-[3/5] bg-gray-200"></div>
                <div className="p-4">
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {dresses.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {dresses.map((dress, index) => (
                  <div
                    key={dress.id}
                    ref={index === dresses.length - 1 ? lastDressElementRef : null}
                  >
                    <DressCard
                      dress={dress}
                      isLiked={likedDressIds.has(dress.id)}
                      onLikeChange={handleLikeChange}
                      showDesigner={true}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500">No dresses available from this designer</p>
              </div>
            )}

            {loadingMore && (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
              </div>
            )}

            {!hasMore && dresses.length > 0 && (
              <p className="text-center text-gray-500 py-8">
                You've reached the end of this designer's collection
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DesignerDetail;